import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Type, RotateCcw, Download, Upload } from 'lucide-react';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';

export const TextCaseConverterTool: React.FC = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [outputs, setOutputs] = useState({
    uppercase: '',
    lowercase: '',
    titleCase: '',
    sentenceCase: '',
    camelCase: '',
    pascalCase: '',
    snakeCase: '',
    kebabCase: '',
    constantCase: '',
    dotCase: '',
    pathCase: '',
    alternatingCase: ''
  });

  const convertText = (text: string) => {
    if (!text) {
      setOutputs({
        uppercase: '',
        lowercase: '',
        titleCase: '',
        sentenceCase: '',
        camelCase: '',
        pascalCase: '',
        snakeCase: '',
        kebabCase: '',
        constantCase: '',
        dotCase: '',
        pathCase: '',
        alternatingCase: ''
      });
      return;
    }

    const results = {
      uppercase: text.toUpperCase(),
      lowercase: text.toLowerCase(),
      titleCase: toTitleCase(text),
      sentenceCase: toSentenceCase(text),
      camelCase: toCamelCase(text),
      pascalCase: toPascalCase(text),
      snakeCase: toSnakeCase(text),
      kebabCase: toKebabCase(text),
      constantCase: toConstantCase(text),
      dotCase: toDotCase(text),
      pathCase: toPathCase(text),
      alternatingCase: toAlternatingCase(text)
    };

    setOutputs(results);
  };

  const toTitleCase = (str: string): string => {
    return str.replace(/\w\S*/g, (txt) => 
      txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  };

  const toSentenceCase = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const toCamelCase = (str: string): string => {
    return str
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => 
        index === 0 ? word.toLowerCase() : word.toUpperCase()
      )
      .replace(/\s+/g, '')
      .replace(/[^a-zA-Z0-9]/g, '');
  };

  const toPascalCase = (str: string): string => {
    return str
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (word) => word.toUpperCase())
      .replace(/\s+/g, '')
      .replace(/[^a-zA-Z0-9]/g, '');
  };

  const toSnakeCase = (str: string): string => {
    return str
      .replace(/\W+/g, ' ')
      .split(/ |\B(?=[A-Z])/)
      .map(word => word.toLowerCase())
      .join('_');
  };

  const toKebabCase = (str: string): string => {
    return str
      .replace(/\W+/g, ' ')
      .split(/ |\B(?=[A-Z])/)
      .map(word => word.toLowerCase())
      .join('-');
  };

  const toConstantCase = (str: string): string => {
    return toSnakeCase(str).toUpperCase();
  };

  const toDotCase = (str: string): string => {
    return str
      .replace(/\W+/g, ' ')
      .split(/ |\B(?=[A-Z])/)
      .map(word => word.toLowerCase())
      .join('.');
  };

  const toPathCase = (str: string): string => {
    return str
      .replace(/\W+/g, ' ')
      .split(/ |\B(?=[A-Z])/)
      .map(word => word.toLowerCase())
      .join('/');
  };

  const toAlternatingCase = (str: string): string => {
    return str
      .split('')
      .map((char, index) => 
        index % 2 === 0 ? char.toLowerCase() : char.toUpperCase()
      )
      .join('');
  };

  const handleInputChange = (value: string) => {
    setInput(value);
    convertText(value);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const downloadResult = (text: string, filename: string) => {
    if (text) {
      const blob = new Blob([text], { type: 'text/plain' });
      saveAs(blob, filename);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInput(content);
        convertText(content);
      };
      reader.readAsText(file);
    }
  };

  const clearAll = () => {
    setInput('');
    setOutputs({
      uppercase: '',
      lowercase: '',
      titleCase: '',
      sentenceCase: '',
      camelCase: '',
      pascalCase: '',
      snakeCase: '',
      kebabCase: '',
      constantCase: '',
      dotCase: '',
      pathCase: '',
      alternatingCase: ''
    });
  };

  const sampleTexts = [
    t('tools.textCaseConverter.sampleTexts.example1'),
    t('tools.textCaseConverter.sampleTexts.example2'),
    t('tools.textCaseConverter.sampleTexts.example3'),
    t('tools.textCaseConverter.sampleTexts.example4'),
    t('tools.textCaseConverter.sampleTexts.example5')
  ];

  const useSample = (sample: string) => {
    setInput(sample);
    convertText(sample);
  };

  const caseTypes = [
    { key: 'uppercase', name: t('tools.textCaseConverter.caseTypes.uppercase.name'), description: t('tools.textCaseConverter.caseTypes.uppercase.description') },
    { key: 'lowercase', name: t('tools.textCaseConverter.caseTypes.lowercase.name'), description: t('tools.textCaseConverter.caseTypes.lowercase.description') },
    { key: 'titleCase', name: t('tools.textCaseConverter.caseTypes.titleCase.name'), description: t('tools.textCaseConverter.caseTypes.titleCase.description') },
    { key: 'sentenceCase', name: t('tools.textCaseConverter.caseTypes.sentenceCase.name'), description: t('tools.textCaseConverter.caseTypes.sentenceCase.description') },
    { key: 'camelCase', name: t('tools.textCaseConverter.caseTypes.camelCase.name'), description: t('tools.textCaseConverter.caseTypes.camelCase.description') },
    { key: 'pascalCase', name: t('tools.textCaseConverter.caseTypes.pascalCase.name'), description: t('tools.textCaseConverter.caseTypes.pascalCase.description') },
    { key: 'snakeCase', name: t('tools.textCaseConverter.caseTypes.snakeCase.name'), description: t('tools.textCaseConverter.caseTypes.snakeCase.description') },
    { key: 'kebabCase', name: t('tools.textCaseConverter.caseTypes.kebabCase.name'), description: t('tools.textCaseConverter.caseTypes.kebabCase.description') },
    { key: 'constantCase', name: t('tools.textCaseConverter.caseTypes.constantCase.name'), description: t('tools.textCaseConverter.caseTypes.constantCase.description') },
    { key: 'dotCase', name: t('tools.textCaseConverter.caseTypes.dotCase.name'), description: t('tools.textCaseConverter.caseTypes.dotCase.description') },
    { key: 'pathCase', name: t('tools.textCaseConverter.caseTypes.pathCase.name'), description: t('tools.textCaseConverter.caseTypes.pathCase.description') },
    { key: 'alternatingCase', name: t('tools.textCaseConverter.caseTypes.alternatingCase.name'), description: t('tools.textCaseConverter.caseTypes.alternatingCase.description') }
  ];

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Type className="w-5 h-5" />
            <span>{t('tools.textCaseConverter.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              onClick={() => input && copyToClipboard(input)}
              disabled={!input}
              variant="outline"
            >
              <Copy className="w-4 h-4 mr-2" />
              {t('common.copy')}
            </Button>
            <Button
              onClick={() => downloadResult(input, 'original.txt')}
              disabled={!input}
              variant="outline"
            >
              <Download className="w-4 h-4 mr-2" />
              {t('common.download')}
            </Button>
            <Button
              onClick={clearAll}
              variant="outline"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              {t('common.clear')}
            </Button>
          </div>

          <div className="mb-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.md"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="mb-2"
            >
              <Upload className="w-4 h-4 mr-2" />
              {t('common.selectFile')}
            </Button>
          </div>

          <Textarea
            value={input}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={t('tools.textCaseConverter.placeholder')}
            className="min-h-[150px]"
          />

          {/* Sample Texts */}
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Sample Texts:
            </h4>
            <div className="flex flex-wrap gap-2">
              {sampleTexts.map((sample, index) => (
                <Button
                  key={index}
                  onClick={() => useSample(sample)}
                  variant="outline"
                  size="sm"
                >
                  {sample}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Output Section */}
      {input && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {caseTypes.map((caseType) => (
            <Card key={caseType.key}>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span>{caseType.name}</span>
                  <div className="flex space-x-1">
                    <Button
                      onClick={() => copyToClipboard(outputs[caseType.key as keyof typeof outputs])}
                      size="sm"
                      variant="outline"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                    <Button
                      onClick={() => downloadResult(outputs[caseType.key as keyof typeof outputs], `${caseType.key}.txt`)}
                      size="sm"
                      variant="outline"
                    >
                      <Download className="w-3 h-3" />
                    </Button>
                  </div>
                </CardTitle>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {caseType.description}
                </p>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md font-mono text-sm break-all">
                  {outputs[caseType.key as keyof typeof outputs] || 'No output'}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Statistics */}
      {input && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.textCaseConverter.textStatistics')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-400">{t('tools.textCaseConverter.characters')}:</span>
                <div className="font-mono">{input.length.toLocaleString()}</div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">{t('tools.textCaseConverter.words')}:</span>
                <div className="font-mono">{input.trim() ? input.trim().split(/\s+/).length : 0}</div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">{t('tools.textCaseConverter.lines')}:</span>
                <div className="font-mono">{input.split('\n').length}</div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">{t('tools.textCaseConverter.paragraphs')}:</span>
                <div className="font-mono">{input.trim() ? input.split(/\n\s*\n/).length : 0}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
